import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, CreditCard, LockKeyhole } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge, badgeVariants } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { trackAnalytics } from "@/lib/analytics";
import { confirmPayment, createPaymentIntent } from "@/lib/mockApi";
import { formatCurrency } from "@/lib/utils";
import type { StepProps } from "@/components/booking/steps/types";
import { StepFrame } from "@/components/booking/steps/StepFrame";

export function PaymentStep({ state, updateState, goBack, startNewBooking }: StepProps) {
  const payment = useMutation({
    mutationFn: async () => {
      if (!state.pricingResult) {
        throw new Error("Pricing is not ready yet.");
      }
      trackAnalytics("payment started", { total: state.pricingResult.totalPayable });
      const intent = await createPaymentIntent(state, state.pricingResult);
      updateState({ paymentStatus: "started", paymentIntentId: intent.id });
      return confirmPayment(intent.id);
    },
    onSuccess: (result) => {
      updateState({ paymentStatus: "succeeded", bookingReference: result.bookingReference });
      trackAnalytics("payment succeeded", { bookingReference: result.bookingReference });
    },
    onError: (error) => {
      updateState({ paymentStatus: "failed" });
      trackAnalytics("payment failed", { error: error instanceof Error ? error.message : "Unknown error" });
    },
  });

  if (state.paymentStatus === "succeeded") {
    const bookingReference = state.bookingReference || "WK-PLACEHOLDER";
    const orderStatusUrl = `/orders/${encodeURIComponent(bookingReference)}`;

    return (
      <StepFrame title="Payment complete" description="Your booking prototype has completed the payment flow and generated a placeholder reference." hideNext>
        <Alert className="border-accent/30 bg-secondary">
          <CheckCircle2 className="h-5 w-5" />
          <AlertTitle>Booking reference</AlertTitle>
          <AlertDescription>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <a className={badgeVariants({ className: "underline-offset-2 hover:underline" })} href={orderStatusUrl}>
                {bookingReference}
              </a>
              <Badge>{state.paymentStatus}</Badge>
            </div>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-foreground">
              Your order is now in progress. You can check your{" "}
              <a className="font-extrabold text-accent underline underline-offset-4" href={orderStatusUrl}>
                booking status page
              </a>{" "}
              at any time for the latest updates on your Waste King booking.
            </p>
          </AlertDescription>
        </Alert>
        <Button type="button" className="mt-6" onClick={startNewBooking}>
          Start a new booking
        </Button>
      </StepFrame>
    );
  }

  return (
    <StepFrame title="Pay" description="This is structured like an embedded Stripe Payment Element. The backend intent and confirmation calls are mocked for now." hideNext>
      <div className="rounded-lg border-2 border-accent/10 bg-white p-5">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="font-black">Secure card payment</h3>
            <p className="text-sm text-muted-foreground">Stripe Elements placeholder</p>
          </div>
          <LockKeyhole className="h-5 w-5 text-accent" />
        </div>
        <div className="grid gap-3">
          <Input value="4242 4242 4242 4242" readOnly disabled aria-label="Card number placeholder" />
          <div className="grid gap-3 sm:grid-cols-2">
            <Input value="12 / 30" readOnly disabled aria-label="Expiry placeholder" />
            <Input value="123" readOnly disabled aria-label="CVC placeholder" />
          </div>
        </div>
        <Button type="button" className="mt-5 hidden w-full sm:inline-flex" size="lg" onClick={() => payment.mutate()} disabled={payment.isPending}>
          <CreditCard className="h-5 w-5" />
          {payment.isPending ? "Processing payment..." : `Pay ${formatCurrency(state.pricingResult?.totalPayable ?? 0)}`}
        </Button>
      </div>
      <div className="fixed inset-x-0 bottom-0 z-40 border-t-2 border-accent/10 bg-white/95 px-4 py-3 shadow-[0_-12px_30px_hsl(229_61%_34%_/_0.16)] backdrop-blur sm:hidden">
        <p className="mb-2 text-center text-xs font-extrabold text-accent">
          Total payable {formatCurrency(state.pricingResult?.totalPayable ?? 0)}
        </p>
        <div className="grid grid-cols-[minmax(0,0.7fr)_minmax(0,1fr)] gap-3">
          <Button type="button" variant="outline" onClick={goBack} className="w-full">
            Back
          </Button>
          <Button type="button" onClick={() => payment.mutate()} disabled={payment.isPending} className="w-full">
            <CreditCard className="h-4 w-4" />
            {payment.isPending ? "Processing..." : "Pay now"}
          </Button>
        </div>
      </div>
      {payment.error ? (
        <Alert variant="destructive" className="mt-4">
          <AlertTitle>Payment failed</AlertTitle>
          <AlertDescription>{payment.error.message}. Your booking progress has been kept so you can retry.</AlertDescription>
        </Alert>
      ) : null}
    </StepFrame>
  );
}
